import { useEffect, useMemo, useRef, useState } from 'react';
import { apiRequest } from '../utils/auth';
import { useEscapeToClose } from '../utils/useEscapeToClose';

// ── Types ────────────────────────────────────────────────────────────────
interface Language {
  code: string;
  label: string;
  nativeLabel: string;
  order: number;
  enabled: boolean;
  isSource: boolean;
}

interface Row {
  key: string;
  order: number;
  values: Record<string, string>;
  // Per-language "changed since last export" markers, persisted server-side.
  dirty?: Record<string, boolean>;
}

interface ImportResult {
  code: string;
  isSource: boolean;
  total: number;
  added: number;
  updated: number;
  removedKeys: string[];
  unknownKeys: string[];
  placeholderMismatches: number;
}

type CellState = 'ok' | 'missing' | 'mismatch';
type FilterMode = 'all' | 'missing' | 'mismatch';

// ── Helpers ──────────────────────────────────────────────────────────────
const placeholderKey = (s: string): string =>
  (s.match(/\{\{[^}]+\}\}/g) || []).map((t) => t.trim()).sort().join('|');

function cellState(row: Row, code: string, source: string): CellState {
  const v = row.values[code];
  if (v === undefined || v === null || v === '') return 'missing';
  // The source language is the reference — it can't mismatch itself.
  if (code === source) return 'ok';
  if (placeholderKey(v) !== placeholderKey(row.values[source] || '')) return 'mismatch';
  return 'ok';
}

function countLeaves(obj: any): number {
  let n = 0;
  for (const k of Object.keys(obj || {})) {
    const v = obj[k];
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) n += countLeaves(v);
    else n += 1;
  }
  return n;
}

function downloadFile(filename: string, content: string) {
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Styles ───────────────────────────────────────────────────────────────
const C = {
  border: 'var(--color-border)',
  panel: 'var(--color-surface)',
  head: 'var(--color-surface-hover)',
  input: 'var(--color-input)',
  text: 'var(--color-text)',
  muted: 'var(--color-muted)',
  blue: 'var(--color-accent-strong)',
  green: 'var(--color-success-strong)',
  red: 'var(--color-danger-strong)',
  redText: 'var(--color-danger)',
  amber: 'var(--color-warning-text)',
};

const btn = (bg: string): React.CSSProperties => ({
  padding: '8px 14px',
  backgroundColor: bg,
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500,
});

export default function LocalizationPage() {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [sourceCode, setSourceCode] = useState('en');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [filterLang, setFilterLang] = useState<string>('any');

  const [editing, setEditing] = useState<{ key: string; code: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState<string | null>(null);

  const [showAddLang, setShowAddLang] = useState(false);
  const [addForm, setAddForm] = useState({ code: '', label: '', nativeLabel: '' });
  const [addBusy, setAddBusy] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingImport, setPendingImport] = useState<{ code: string; data: any; keyCount: number } | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importReport, setImportReport] = useState<ImportResult | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiRequest<{
        success: boolean;
        languages: Language[];
        sourceCode: string;
        translations: Row[];
      }>('/api/admin/i18n/translations');
      setLanguages(res.languages || []);
      setSourceCode(res.sourceCode || 'en');
      setRows(res.translations || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load translations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const nonSource = useMemo(
    () => languages.filter((l) => l.code !== sourceCode).sort((a, b) => a.order - b.order),
    [languages, sourceCode],
  );

  // Grid columns: source language first, then the rest. English is editable too.
  const orderedLangs = useMemo(() => {
    const src = languages.find((l) => l.code === sourceCode);
    return src ? [src, ...nonSource] : nonSource;
  }, [languages, sourceCode, nonSource]);

  const stats = useMemo(() => {
    const s: Record<string, { missing: number; mismatch: number }> = {};
    for (const l of nonSource) s[l.code] = { missing: 0, mismatch: 0 };
    for (const r of rows) {
      for (const l of nonSource) {
        const st = cellState(r, l.code, sourceCode);
        if (st === 'missing') s[l.code].missing++;
        else if (st === 'mismatch') s[l.code].mismatch++;
      }
    }
    return s;
  }, [rows, nonSource, sourceCode]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const langsToCheck = filterLang === 'any' ? nonSource.map((l) => l.code) : [filterLang];
    return rows.filter((r) => {
      if (q) {
        const inKey = r.key.toLowerCase().includes(q);
        const inVal = Object.values(r.values).some((v) => (v || '').toLowerCase().includes(q));
        if (!inKey && !inVal) return false;
      }
      if (filterMode === 'missing') return langsToCheck.some((c) => cellState(r, c, sourceCode) === 'missing');
      if (filterMode === 'mismatch') return langsToCheck.some((c) => cellState(r, c, sourceCode) === 'mismatch');
      return true;
    });
  }, [rows, search, filterMode, filterLang, nonSource, sourceCode]);

  // ── Cell editing ────────────────────────────────────────────────────────
  const startEdit = (key: string, code: string, current: string) => {
    setEditing({ key, code });
    setEditValue(current || '');
  };

  const saveCell = async (key: string, code: string, value: string) => {
    const row = rows.find((r) => r.key === key);
    if (!row) return;
    if ((row.values[code] || '') === value) {
      setEditing(null);
      return;
    }
    const cellId = `${key}|${code}`;
    setSavingCell(cellId);
    try {
      await apiRequest(`/api/admin/i18n/translations/${encodeURIComponent(key)}`, {
        method: 'PUT',
        body: JSON.stringify({ code, value }),
      });
      setRows((prev) =>
        prev.map((r) =>
          r.key === key
            ? { ...r, values: { ...r.values, [code]: value }, dirty: { ...(r.dirty || {}), [code]: true } }
            : r,
        ),
      );
      setEditing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save translation');
    } finally {
      setSavingCell(null);
    }
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const exportLang = async (code: string) => {
    try {
      setNotice(null);
      const res = await apiRequest<{ success: boolean; filename: string; content: string; missingKeys: string[] }>(
        `/api/admin/i18n/export/${code}`,
      );
      downloadFile(res.filename, res.content);
      if (res.missingKeys && res.missingKeys.length > 0) {
        setNotice(
          `Downloaded ${res.filename}. ⚠ ${res.missingKeys.length} key(s) had no ${code} translation and fell back to English in the file.`,
        );
      } else {
        setNotice(`Downloaded ${res.filename} — all keys translated. Replace it in MobileApp/i18n/locales/ and commit.`);
      }
      // Exporting clears this language's pending-change markers server-side —
      // refresh so the highlights update.
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export');
    }
  };

  // ── Import ────────────────────────────────────────────────────────────────
  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const inferred = file.name.replace(/\.json$/i, '').toLowerCase();
      setPendingImport({ code: inferred, data, keyCount: countLeaves(data) });
    } catch {
      setError('That file is not valid JSON.');
    }
  };

  const confirmImport = async () => {
    if (!pendingImport) return;
    setImportBusy(true);
    try {
      const res = await apiRequest<ImportResult & { success: boolean }>('/api/admin/i18n/import', {
        method: 'POST',
        body: JSON.stringify({ code: pendingImport.code, data: pendingImport.data }),
      });
      setPendingImport(null);
      setImportReport(res);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import');
    } finally {
      setImportBusy(false);
    }
  };

  // ── Add language ───────────────────────────────────────────────────────────
  const submitAddLang = async () => {
    const code = addForm.code.trim().toLowerCase();
    if (!code || !addForm.label.trim() || !addForm.nativeLabel.trim()) {
      setError('Language code, English name, and native name are all required.');
      return;
    }
    setAddBusy(true);
    try {
      await apiRequest('/api/admin/i18n/languages', {
        method: 'POST',
        body: JSON.stringify({ code, label: addForm.label.trim(), nativeLabel: addForm.nativeLabel.trim() }),
      });
      setShowAddLang(false);
      setAddForm({ code: '', label: '', nativeLabel: '' });
      setNotice(
        `Language "${code}" added. ⚠ Before it ships, a developer must register ${code}.json in the mobile app ` +
          `(i18n/translations.ts + i18n/languages.ts).`,
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add language');
    } finally {
      setAddBusy(false);
    }
  };

  if (loading && rows.length === 0) {
    return (
      <div className="page-status">Loading translations...</div>
    );
  }

  const th: React.CSSProperties = {
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: 600,
    borderBottom: `2px solid ${C.border}`,
    color: C.text,
    position: 'sticky',
    top: 0,
    backgroundColor: C.head,
    zIndex: 2,
    whiteSpace: 'nowrap',
  };

  return (
    <div className="page">
      {/* Header */}
      <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Localization</h1>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <input ref={fileInputRef} type="file" accept=".json,application/json" onChange={onPickFile} style={{ display: 'none' }} />
          <button onClick={() => fileInputRef.current?.click()} style={btn(C.blue)}>⬆ Import .json</button>
          <button onClick={() => setShowAddLang(true)} style={btn('#6e40c9')}>+ Add Language</button>
        </div>
      </div>

      {/* How-it-works note */}
      <div style={{ marginBottom: '16px', padding: '10px 14px', backgroundColor: '#132132', border: '1px solid var(--color-accent-border)', borderRadius: '6px', color: C.muted, fontSize: '13px', lineHeight: 1.5 }}>
        Click a cell to edit it — including English — then confirm with <b style={{ color: C.text }}>✓</b> (or press Esc / <b style={{ color: C.text }}>✕</b> to discard); nothing is saved until you confirm.
        A cell with an <span style={{ display: 'inline-block', width: '7px', height: '7px', borderRadius: '50%', backgroundColor: C.amber, verticalAlign: 'middle', margin: '0 2px' }} /> amber dot has a saved change that hasn't been exported yet — <b style={{ color: C.text }}>Export</b> that language to clear it. English is the source the other languages fall back
        to; developers also maintain it in code, so if you edit English here,
        <b style={{ color: C.text }}> Export</b> <code>en.json</code> and commit it so the app code stays in sync (otherwise a later
        <b style={{ color: C.text }}> Import .json</b> from code overwrites your English edits). Use
        <b style={{ color: C.text }}> Import .json</b> to pull in new keys after developers add them. When done,
        <b style={{ color: C.text }}> Export</b> each language and replace the file in <code>MobileApp/i18n/locales/</code>, then commit.
        Keep <code style={{ color: C.amber }}>{'{{placeholders}}'}</code> exactly as they appear in English.
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: 'var(--color-danger-surface)', color: C.redText, borderRadius: '4px', marginBottom: '16px' }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', color: C.redText, cursor: 'pointer' }}>✕</button>
        </div>
      )}
      {notice && (
        <div style={{ padding: '12px', backgroundColor: '#12261a', color: 'var(--color-success)', borderRadius: '4px', marginBottom: '16px' }}>
          {notice}
          <button onClick={() => setNotice(null)} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--color-success)', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Per-language status */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
        {nonSource.map((l) => {
          const s = stats[l.code] || { missing: 0, mismatch: 0 };
          return (
            <div key={l.code} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', backgroundColor: C.panel, border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '13px' }}>
              <span style={{ color: C.text, fontWeight: 600 }}>{l.nativeLabel}</span>
              <span style={{ color: C.muted }}>({l.code})</span>
              <span style={{ color: s.missing ? C.redText : 'var(--color-success)' }}>{s.missing} missing</span>
              {s.mismatch > 0 && <span style={{ color: C.amber }}>{s.mismatch} placeholder ⚠</span>}
              <button onClick={() => exportLang(l.code)} style={{ ...btn(C.green), padding: '4px 10px', fontSize: '12px' }}>Export</button>
            </div>
          );
        })}
        {/* Source export too (in case en is edited upstream and needs regenerating) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', backgroundColor: C.panel, border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '13px' }}>
          <span style={{ color: C.text, fontWeight: 600 }}>English</span>
          <span style={{ color: C.muted }}>(source)</span>
          <button onClick={() => exportLang(sourceCode)} style={{ ...btn('var(--color-border)'), padding: '4px 10px', fontSize: '12px' }}>Export</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '12px', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search key or text..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '8px 12px', border: `1px solid ${C.border}`, backgroundColor: C.input, color: C.text, borderRadius: '4px', fontSize: '14px', flex: '1 1 240px', minWidth: 0 }}
        />
        <select value={filterMode} onChange={(e) => setFilterMode(e.target.value as FilterMode)} style={{ padding: '8px', backgroundColor: C.input, color: C.text, border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '14px' }}>
          <option value="all">All keys</option>
          <option value="missing">Only missing</option>
          <option value="mismatch">Only placeholder mismatch</option>
        </select>
        <select value={filterLang} onChange={(e) => setFilterLang(e.target.value)} style={{ padding: '8px', backgroundColor: C.input, color: C.text, border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '14px' }}>
          <option value="any">Any language</option>
          {nonSource.map((l) => (
            <option key={l.code} value={l.code}>{l.nativeLabel} ({l.code})</option>
          ))}
        </select>
        <span style={{ color: C.muted, fontSize: '13px' }}>{filtered.length} of {rows.length} keys</span>
      </div>

      {/* Grid */}
      <div style={{ backgroundColor: C.panel, borderRadius: '8px', overflow: 'auto', maxHeight: 'calc(100vh - 320px)', border: `1px solid ${C.border}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr>
              <th style={{ ...th, left: 0, zIndex: 3, minWidth: '220px' }}>Key</th>
              {orderedLangs.map((l) => (
                <th key={l.code} style={{ ...th, minWidth: '220px' }}>
                  {l.code === sourceCode ? 'English' : l.nativeLabel}{' '}
                  <span style={{ color: C.muted, fontWeight: 400 }}>({l.code}{l.code === sourceCode ? ', source' : ''})</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.key} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: C.muted, verticalAlign: 'top', position: 'sticky', left: 0, backgroundColor: C.panel, whiteSpace: 'nowrap' }}>{r.key}</td>
                {orderedLangs.map((l) => {
                  const isSource = l.code === sourceCode;
                  const isEditing = editing?.key === r.key && editing?.code === l.code;
                  // A cell is "being changed" while it's open for editing; deepen the
                  // tint once the entered value actually differs from what's saved.
                  const isDirty = isEditing && editValue !== (r.values[l.code] || '');
                  const st = cellState(r, l.code, sourceCode);
                  const cellId = `${r.key}|${l.code}`;
                  // Persisted "changed since last export" flag (survives reload).
                  const pendingExport = !!(r.dirty && r.dirty[l.code]);
                  const bg = isEditing
                    ? (isDirty ? 'rgba(210,153,34,0.28)' : 'rgba(210,153,34,0.12)')
                    : st === 'missing' ? 'rgba(248,81,73,0.10)'
                    : pendingExport ? 'rgba(210,153,34,0.10)'
                    : isSource ? 'rgba(56,139,253,0.06)'
                    : 'transparent';
                  const border = st === 'mismatch' ? `1px solid ${C.amber}` : '1px solid transparent';
                  return (
                    <td key={l.code} style={{ padding: '4px', verticalAlign: 'top', backgroundColor: bg }}>
                      {isEditing ? (
                        <div>
                          <textarea
                            autoFocus
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') { e.preventDefault(); setEditing(null); }
                              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveCell(r.key, l.code, editValue); }
                            }}
                            rows={Math.min(6, Math.max(2, Math.ceil(editValue.length / 40)))}
                            style={{ width: '100%', boxSizing: 'border-box', padding: '6px', backgroundColor: C.input, color: C.text, border: `1px solid ${C.blue}`, borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical' }}
                          />
                          {/* Explicit confirm / cancel — nothing is saved until ✓ is pressed. */}
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '4px' }}>
                            <button
                              onClick={() => saveCell(r.key, l.code, editValue)}
                              disabled={savingCell === cellId}
                              title="Confirm change (⌘/Ctrl+Enter)"
                              style={{ padding: '2px 10px', backgroundColor: C.green, color: 'white', border: 'none', borderRadius: '4px', cursor: savingCell === cellId ? 'default' : 'pointer', fontSize: '13px', lineHeight: 1.4, opacity: savingCell === cellId ? 0.6 : 1 }}
                            >
                              {savingCell === cellId ? '…' : '✓'}
                            </button>
                            <button
                              onClick={() => setEditing(null)}
                              disabled={savingCell === cellId}
                              title="Cancel (Esc)"
                              style={{ padding: '2px 10px', backgroundColor: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: '4px', cursor: savingCell === cellId ? 'default' : 'pointer', fontSize: '13px', lineHeight: 1.4, opacity: savingCell === cellId ? 0.6 : 1 }}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() => startEdit(r.key, l.code, r.values[l.code])}
                          title={pendingExport ? 'Changed — not yet exported' : 'Click to edit'}
                          style={{ position: 'relative', minHeight: '20px', padding: '4px 8px', border, borderRadius: '4px', cursor: 'text', color: st === 'missing' ? C.redText : C.text, whiteSpace: 'pre-wrap', fontStyle: st === 'missing' ? 'italic' : 'normal' }}
                        >
                          {pendingExport && (
                            <span
                              title="Changed — not yet exported"
                              style={{ position: 'absolute', top: '3px', right: '3px', width: '7px', height: '7px', borderRadius: '50%', backgroundColor: C.amber }}
                            />
                          )}
                          {savingCell === cellId ? '…saving' : st === 'missing' ? '— missing —' : r.values[l.code]}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={2 + nonSource.length} style={{ padding: '30px', textAlign: 'center', color: C.muted }}>
                  {rows.length === 0 ? 'No translations yet — use “Import .json” to load en.json (and the other locale files) to get started.' : 'No keys match the current filter.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Language modal */}
      {showAddLang && (
        <Modal onClose={() => setShowAddLang(false)} title="Add Language">
          <Field label="Language code (e.g. fr, es, zh-cn)">
            <input value={addForm.code} onChange={(e) => setAddForm({ ...addForm, code: e.target.value })} placeholder="fr" style={inputStyle} />
          </Field>
          <Field label="English name (e.g. French)">
            <input value={addForm.label} onChange={(e) => setAddForm({ ...addForm, label: e.target.value })} placeholder="French" style={inputStyle} />
          </Field>
          <Field label="Native name (e.g. Français)">
            <input value={addForm.nativeLabel} onChange={(e) => setAddForm({ ...addForm, nativeLabel: e.target.value })} placeholder="Français" style={inputStyle} />
          </Field>
          <div style={{ fontSize: '12px', color: C.amber, marginBottom: '16px' }}>
            ⚠ A new language also needs a one-time code change in the mobile app before it ships.
          </div>
          <ModalActions>
            <button onClick={() => setShowAddLang(false)} style={btn('var(--color-subtle)')}>Cancel</button>
            <button onClick={submitAddLang} disabled={addBusy} style={{ ...btn(C.green), opacity: addBusy ? 0.6 : 1 }}>{addBusy ? 'Adding…' : 'Add'}</button>
          </ModalActions>
        </Modal>
      )}

      {/* Import confirm modal */}
      {pendingImport && (
        <Modal onClose={() => setPendingImport(null)} title="Import translations">
          <div style={{ marginBottom: '16px', color: C.text, fontSize: '14px', lineHeight: 1.6 }}>
            File contains <b>{pendingImport.keyCount}</b> keys. Import as language:
          </div>
          <Field label="Language">
            <select value={pendingImport.code} onChange={(e) => setPendingImport({ ...pendingImport, code: e.target.value })} style={inputStyle}>
              {languages.map((l) => (
                <option key={l.code} value={l.code}>{l.nativeLabel} ({l.code}){l.isSource ? ' — source' : ''}</option>
              ))}
              {!languages.some((l) => l.code === pendingImport.code) && (
                <option value={pendingImport.code}>{pendingImport.code} (new language)</option>
              )}
            </select>
          </Field>
          <div style={{ fontSize: '12px', color: pendingImport.code === sourceCode ? C.amber : C.muted, marginBottom: '16px', lineHeight: 1.5 }}>
            {pendingImport.code === sourceCode
              ? '⚠ Importing English syncs the key set: it adds new keys, updates English text, and DELETES keys that are no longer in this file (old/unused keys — and their translations in every language — are removed). Translations for keys that still exist are kept.'
              : 'Only fills translations for keys that already exist. New keys must come from an English import first.'}
          </div>
          <ModalActions>
            <button onClick={() => setPendingImport(null)} style={btn('var(--color-subtle)')}>Cancel</button>
            <button onClick={confirmImport} disabled={importBusy} style={{ ...btn(C.blue), opacity: importBusy ? 0.6 : 1 }}>{importBusy ? 'Importing…' : 'Import'}</button>
          </ModalActions>
        </Modal>
      )}

      {/* Import report modal */}
      {importReport && (
        <Modal onClose={() => setImportReport(null)} title="Import complete">
          <div style={{ color: C.text, fontSize: '14px', lineHeight: 1.8 }}>
            <div>Language: <b>{importReport.code}</b>{importReport.isSource ? ' (source)' : ''}</div>
            <div>Total keys in file: {importReport.total}</div>
            {importReport.isSource && <div style={{ color: 'var(--color-success)' }}>Added: {importReport.added} · Updated: {importReport.updated}</div>}
            {!importReport.isSource && <div style={{ color: 'var(--color-success)' }}>Filled/updated: {importReport.updated}</div>}
            {importReport.placeholderMismatches > 0 && <div style={{ color: C.amber }}>Placeholder mismatches: {importReport.placeholderMismatches}</div>}
            {importReport.removedKeys.length > 0 && (
              <div style={{ color: C.amber }}>{importReport.removedKeys.length} stale key(s) removed (no longer in the English file).</div>
            )}
            {importReport.unknownKeys.length > 0 && (
              <div style={{ color: C.muted }}>{importReport.unknownKeys.length} key(s) in the file don’t exist yet (ignored — import en.json first).</div>
            )}
          </div>
          <ModalActions>
            <button onClick={() => setImportReport(null)} style={btn(C.blue)}>Done</button>
          </ModalActions>
        </Modal>
      )}
    </div>
  );
}

// ── Small presentational helpers ───────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px',
  border: `1px solid ${C.border}`,
  backgroundColor: C.input,
  color: C.text,
  borderRadius: '4px',
  fontSize: '14px',
};

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  // Only rendered while open, so the layer is always live here.
  useEscapeToClose(true, onClose);
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: C.panel, borderRadius: '8px', padding: '24px', width: '460px', maxWidth: '92%' }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px', color: C.text }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: C.text, fontSize: '13px' }}>{label}</label>
      {children}
    </div>
  );
}

function ModalActions({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>{children}</div>;
}
